import * as i0 from '@angular/core';
import { InjectionToken, EventEmitter, Component, Optional, Inject, Input, Output, ViewChild, NgModule } from '@angular/core';
import * as i4 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i5 from '@angular/material/menu';
import { MatMenuModule } from '@angular/material/menu';
import * as i6 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import * as i7 from 'angular-gridster2';
import { CompactType, GridType, GridsterModule } from 'angular-gridster2';
import * as i13 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i15 from '@angular/material/select';
import { MatSelectModule } from '@angular/material/select';
import * as i14 from '@angular/material/form-field';
import { MatFormFieldModule } from '@angular/material/form-field';
import * as i10 from '@azavista/components/button';
import { AzavistaButtonModule } from '@azavista/components/button';
import * as i11 from '@azavista/components/widgets';
import { AzavistaWidgetDialogComponent, AzavistaWidgetsModule } from '@azavista/components/widgets';
import { AzavistaMultiSelectDialogComponent, AzavistaMultiSelectDialogModule } from '@azavista/components/multi-select-dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import bsonObjectId from 'bson-objectid';
import * as i12 from '@azavista/components/exported-element';
import { AzavistaExportedElementExporterDirective, AzavistaExportedElementModule } from '@azavista/components/exported-element';
import * as i1 from '@angular/material/dialog';
import * as i2 from '@angular/router';
import * as i3 from '@azavista/components/shared';
import { AzavistaSharedModule } from '@azavista/components/shared';
import * as i8 from '@angular/forms';
import * as i9 from '@ngx-translate/core';
import * as i16 from '@angular/material/core';

const AZAVISTA_COMPONENTS_DASHBOARD_DEFAULT_OPTIONS = new InjectionToken('azavista-components-dashboard-default-options');
const NG_GRIDSTER_COMPACT_OPTIONS = [
    { value: CompactType.None, label: 'NONE' },
    { value: CompactType.CompactUp, label: 'UP' },
    { value: CompactType.CompactUpAndLeft, label: 'UP_AND_LEFT' },
    { value: CompactType.CompactUpAndRight, label: 'UP_AND_RIGHT' },
    { value: CompactType.CompactRight, label: 'RIGHT' },
    { value: CompactType.CompactRightAndUp, label: 'RIGHT_AND_UP' },
    { value: CompactType.CompactLeft, label: 'LEFT' },
    { value: CompactType.CompactLeftAndUp, label: 'LEFT_AND_UP' }
];

class DashboardComponent extends AzavistaExportedElementExporterDirective {
    constructor(matDialog, router, translateSvc, ngZone, dеfaultOptions) {
        super(ngZone);
        this.matDialog = matDialog;
        this.router = router;
        this.translateSvc = translateSvc;
        this.ngZone = ngZone;
        this.dеfaultOptions = dеfaultOptions;
        this.dashboardTitle = '';
        this.availableWidgets = [];
        this.layout = [];
        this.widgets = [];
        this.widgetDataLoadingImageUrl = '';
        this.disableAddWidget = false;
        this.disableEditDashboard = false;
        this.disableCreateDashboard = false;
        this.dashboardChanged = new EventEmitter();
        this.dashboardSelected = new EventEmitter();
        this.editDashboard = new EventEmitter();
        this.createDashboard = new EventEmitter();
        this.widgetCustomEvent = new EventEmitter();
        this.widgetsInfo = [];
        this.compactOptions = NG_GRIDSTER_COMPACT_OPTIONS;
        this.itemsChanged$ = new Subject();
        this.destroyed$ = new Subject();
    }
    async ngOnInit() {
        this.selectedDashboard = this.dashboards.find(x => x.id === this.activeDashboardId);
        this.applyDefaultOptions();
        this.unifyLayoutAndWidgets();
        this.defaultGridsterConfig = {
            itemChangeCallback: (item, itemComponent) => this.onItemChange(item, itemComponent),
            itemResizeCallback: (item, itemComponent) => this.onItemResize(item, itemComponent),
            gridSizeChangedCallback: (gridster) => this.onGridSizeChanged(gridster),
            resizable: { enabled: true },
            draggable: { enabled: true },
            gridType: GridType.Fixed,
            fixedColWidth: 385,
            fixedRowHeight: 300,
            compactType: CompactType.CompactUpAndLeft,
            setGridSize: true,
            minCols: 4,
            maxCols: 4,
            minRows: 4,
            maxRows: 10,
            pushItems: true,
            ...this.gridsterConfig
        };
        this.itemsChanged$
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
            next: () => {
                // If this.layout has less items than this.gridster.grid, then widget was removed
                // and grid will reflect this after the Angular change detection
                // In this case we must use this.layout since it is the correct one, not what we see on the screen
                // In other cases we need this.gridster.grid since it could be item resize or move where the count
                // of the two lengths will be the same
                const layout = this.getLayout();
                this.dashboardChanged.emit({
                    layout,
                    widgets: this.widgets,
                    compactType: this.defaultGridsterConfig.compactType
                });
            }
        });
    }
    ngOnChanges(changes) {
        if (changes.layout || changes.widgets) {
            this.unifyLayoutAndWidgets();
        }
    }
    getLayout() {
        return this.layout.length < this.gridster.grid.length ? this.layout : this.gridster.grid.map(x => x.$item);
    }
    changedOptions() {
        if (this.defaultGridsterConfig.api?.optionsChanged) {
            this.defaultGridsterConfig.api?.optionsChanged();
        }
    }
    onWidgetAlignmentChanged() {
        this.changedOptions();
        this.dashboardChanged.emit({
            widgets: this.widgets,
            layout: this.getLayout(),
            compactType: this.defaultGridsterConfig.compactType,
        });
    }
    viewDetails(selectedWidget) {
        const widgetDefinition = {
            ...this.widgets[selectedWidget]
        };
        if (widgetDefinition.viewDetailsNavPath) {
            this.router.navigate([widgetDefinition.viewDetailsNavPath], {
                queryParams: { ...widgetDefinition.viewDetailsNavQueryParams }
            });
        }
    }
    getViewButtonText(selectedWidget) {
        const widgetDefinition = {
            ...this.widgets[selectedWidget]
        };
        return widgetDefinition.viewButtonText || this.translateSvc.getTranslation('VIEW_DETAILS');
    }
    onDashboardSelected(dashboard) {
        this.dashboardSelected.emit(dashboard);
    }
    onCreateDashboardClicked() {
        this.createDashboard.emit();
    }
    onEditDashboardClicked() {
        this.editDashboard.emit(this.selectedDashboard);
    }
    unifyLayoutAndWidgets() {
        if (this.layout.length === this.widgets.length) {
            // Layout contains exact number of items as widgets - no unification is needed
            return;
        }
        // Dashboard cannot work with inconsistent count of layout items and widgets
        // Both arrays must have equal lengths according to the shorter array
        const shorterLength = Math.min(this.layout.length, this.widgets.length);
        this.layout = this.layout.slice(0, shorterLength);
        this.widgets = this.widgets.slice(0, shorterLength);
    }
    applyDefaultOptions() {
        if (this.dеfaultOptions) {
            this.widgetDataLoadingImageUrl = this.widgetDataLoadingImageUrl || this.dеfaultOptions.widgetDataLoadingImageUrl;
        }
    }
    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
    widgetMenuSelected(index) {
        this.selectedWidgetMenuIndex = index;
    }
    getWidgetInfo(index) {
        const widgetId = this.widgets[index].id;
        let widgetInfo = this.widgetsInfo.find(x => x.id === widgetId);
        if (!widgetInfo) {
            widgetInfo = { id: widgetId, manualyAdded: false, loading: false };
            this.widgetsInfo.push(widgetInfo);
        }
        return widgetInfo;
    }
    onWidgetLoading(widgetDefinitionIndex, loading) {
        const widgetInfo = this.getWidgetInfo(widgetDefinitionIndex);
        window.setTimeout(() => {
            widgetInfo.loading = loading;
        });
    }
    onWidgetCustomEvent(widgetDefinitionIndex, args) {
        const widgetComponentDefinition = this.widgets[widgetDefinitionIndex];
        this.widgetCustomEvent.next({ widgetComponentDefinition: widgetComponentDefinition, widgetCustomEventArgs: args });
    }
    onSettingsNeeded(index) {
        // Show widget config only for those that were manually added
        // Other widgets could also report settings needed but those are probably already existing
        // with empty settings that came from the server - in order to avoid settings dialog for these
        // only manually added will show their settings dialog
        const widgetInfo = this.getWidgetInfo(index);
        if (widgetInfo.manualyAdded) {
            this.showWidgetConfig(index).afterClosed().subscribe({
                next: dialogResult => {
                    if (!dialogResult || dialogResult.action === 'close') {
                        // Dialog was closed wihout providing settings
                        // We must to remove this widget
                        this.deleteSelectedWidget(index);
                    }
                }
            });
        }
    }
    addWidget() {
        const data = {
            title: this.translateSvc.getTranslation("CHOOSE_AN_EXISTING_WIDGET_DIALOG_TITLE" /* Translations.chooseAnExistingWidgetDialogTitle */),
            subTitle: this.translateSvc.getTranslation("SELECT_A_REPORTING_WIDGET_DIALOG_SUBTITLE" /* Translations.chooseAnExistingWidgetDialogSubtitle */),
            items: [...this.availableWidgets].map(item => ({ ...item })),
            singleSelection: true
        };
        this.matDialog.open(AzavistaMultiSelectDialogComponent, {
            data: data,
            minWidth: '40%',
            maxWidth: '80%'
        })
            .afterClosed()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
            next: (value) => this.processAddWigetDialogResult(value)
        });
    }
    processAddWigetDialogResult(value) {
        if (!value) {
            return;
        }
        value.items
            .filter(x => x.selected)
            .forEach((item) => {
            const widget = {
                ...item.definition,
                id: bsonObjectId().toHexString(),
                dataChangedSubject: new Subject(),
                settings: {
                    local: {
                        ...item.definition.settings && item.definition.settings.local
                    },
                    remote: {
                        ...item.definition.settings && item.definition.settings.remote
                    }
                }
            };
            this.layout.push({});
            this.widgets.push(widget);
            this.widgetsInfo.push({ id: widget.id, manualyAdded: true, loading: false });
            this.itemsChanged$.next();
        });
    }
    showWidgetConfig(selectedWidget) {
        const data = {
            actionSubject: new Subject(),
            widgetDefinition: {
                ...this.widgets[selectedWidget]
            },
            settingsAreValid: false
        };
        const dialogRef = this.matDialog.open(AzavistaWidgetDialogComponent, {
            data: data, minWidth: '700px'
        });
        const subscription$ = data.actionSubject.subscribe({
            next: (dialogResult) => {
                subscription$.unsubscribe();
                dialogRef.close(dialogResult);
                if (dialogResult.action === 'save') {
                    this.onWidgetSettingsChanged({
                        definition: dialogResult.data.widgetDefinition,
                        settings: dialogResult.data.widgetDefinition.settings,
                        settingsAreValid: dialogResult.data.settingsAreValid
                    });
                }
            }
        });
        return dialogRef;
    }
    deleteSelectedWidget(selectedWidget) {
        // gridster.grid is changed by the gridster itself when the array for "<gridster-item *ngFor="let item of layout..."
        // is changed from the code - we don't need to manually change it because this causes troubles by removing
        // also the item before the removed one from this.layout
        // this.gridster.grid = this.gridster.grid.filter((_, index) => selectedWidget !== index);
        this.layout = this.layout.filter((_, index) => selectedWidget !== index);
        this.widgets = this.widgets.filter((_, index) => selectedWidget !== index);
        this.widgetsInfo = this.widgetsInfo.filter((_, index) => selectedWidget !== index);
        this.itemsChanged$.next();
    }
    onWidgetSettingsChanged(changedValue) {
        const index = this.widgets.findIndex(x => x.id === changedValue.definition.id);
        const definition = this.widgets[index];
        if (!definition) {
            return;
        }
        definition.title = changedValue.definition.title;
        definition.settings = { ...changedValue.settings };
        definition.viewDetailsNavPath = changedValue.definition.viewDetailsNavPath;
        definition.viewDetailsNavQueryParams = changedValue.definition.viewDetailsNavQueryParams;
        definition.viewButtonText = changedValue.definition.viewButtonText;
        definition.dataChangedSubject.next({
            settings: changedValue.settings,
            title: changedValue.definition.title,
            viewDetailsNavPath: changedValue.definition.viewDetailsNavPath,
            viewDetailsNavQueryParams: changedValue.definition.viewDetailsNavQueryParams,
            viewButtonText: changedValue.definition.viewButtonText
        });
        this.itemsChanged$.next();
    }
    onGridSizeChanged(gr) {
    }
    onItemChange(item, itemComponent) {
        // This is the place where we should save the dashboard
        // It could be called multiple times so only the last one must be used
        // Use observable that emits the last value only when there are no emits for more than a second
        this.itemsChanged$.next();
    }
    onItemResize(item, itemComponent) {
        const index = this.layout.findIndex(x => x === item);
        const newItem = itemComponent.$item;
        const unusedHeight = 32 + 12;
        const unusedWidth = 12;
        const cellWidth = (newItem.cols || 1) * this.gridster.curColWidth;
        const cellHeight = (newItem.rows || 1) * this.gridster.curRowHeight;
        const newWidth = cellWidth - unusedWidth;
        const newHeight = cellHeight - unusedHeight;
        const definition = this.widgets[index];
        definition.dataChangedSubject.next({
            width: newWidth,
            height: newHeight
        });
        definition.size = {
            width: newWidth,
            height: newHeight
        };
        this.itemsChanged$.next();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: DashboardComponent, deps: [{ token: i1.MatDialog }, { token: i2.Router }, { token: i3.TranslationService }, { token: i0.NgZone }, { token: AZAVISTA_COMPONENTS_DASHBOARD_DEFAULT_OPTIONS, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.10", type: DashboardComponent, selector: "azavista-dashboard", inputs: { dashboardTitle: "dashboardTitle", activeDashboardId: "activeDashboardId", dashboards: "dashboards", gridsterConfig: "gridsterConfig", availableWidgets: "availableWidgets", layout: "layout", widgets: "widgets", widgetDataLoadingImageUrl: "widgetDataLoadingImageUrl", disableAddWidget: "disableAddWidget", disableEditDashboard: "disableEditDashboard", disableCreateDashboard: "disableCreateDashboard", disabledWidgetActions: "disabledWidgetActions" }, outputs: { dashboardChanged: "dashboardChanged", dashboardSelected: "dashboardSelected", editDashboard: "editDashboard", createDashboard: "createDashboard", widgetCustomEvent: "widgetCustomEvent" }, viewQueries: [{ propertyName: "gridster", first: true, predicate: ["gridster"], descendants: true, static: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div *ngIf=\"dashboardTitle\" class=\"title-container aza-cmp-flex-row aza-cmp-content-space-between\">\r\n    <h1 class=\"dashboard-title\">{{ dashboardTitle }}</h1>\r\n    <ng-container *ngTemplateOutlet=\"addWidgetButtonTemplate\"></ng-container>\r\n</div>\r\n\r\n<azavista-exported-element-wrapper>\r\n    <div class=\"actions-container dashboard-actions-container aza-cmp-flex-row aza-cmp-content-space-between aza-cmp-rounded-border\">\r\n        <div class=\"dashboard-selector aza-cmp-flex-row\">\r\n            <mat-form-field class=\"aza-cmp-medium-width\">\r\n                <mat-label><span translate>DASHBOARD</span></mat-label>\r\n                <mat-select [(ngModel)]=\"selectedDashboard\" (selectionChange)=\"onDashboardSelected($event.value)\">\r\n                    <mat-option *ngFor=\"let dashboard of dashboards\" [value]=\"dashboard\">{{ dashboard.name }}</mat-option>\r\n                </mat-select>\r\n            </mat-form-field>\r\n            <azavista-flat-button\r\n                [disabled]=\"disableEditDashboard || !selectedDashboard || !dashboards.length\" (click)=\"onEditDashboardClicked()\"\r\n                color=\"primary\">\r\n                <mat-icon class=\"material-icons-outlined\">edit</mat-icon><span>{{ 'EDIT_DASHBOARD' | translate }}</span>\r\n            </azavista-flat-button>\r\n            <azavista-flat-button (click)=\"onCreateDashboardClicked()\" color=\"primary\" [disabled]=\"disableCreateDashboard\">\r\n                <mat-icon class=\"material-icons-outlined\">add</mat-icon><span>{{ 'CREATE_DASHBOARD' | translate }}</span>\r\n            </azavista-flat-button>\r\n        </div>\r\n        <ng-container *ngIf=\"!dashboardTitle\">\r\n            <ng-container *ngTemplateOutlet=\"addWidgetButtonTemplate\"></ng-container>\r\n        </ng-container>\r\n        <div class=\"dashboard-config-options\">\r\n            <mat-form-field>\r\n                <mat-label><span translate>WIDGETS_ALIGNMENT</span></mat-label>\r\n                <mat-select [(ngModel)]=\"defaultGridsterConfig.compactType\" (ngModelChange)=\"onWidgetAlignmentChanged()\">\r\n                    <mat-option *ngFor=\"let compactOption of compactOptions\" [value]=\"compactOption.value\">\r\n                      {{ compactOption.label | translate }}\r\n                    </mat-option>\r\n                </mat-select>\r\n            </mat-form-field>\r\n        </div>\r\n    </div>\r\n</azavista-exported-element-wrapper>\r\n\r\n<p *ngIf=\"!dashboards.length\" class=\"no-dashboards-warning\"><span translate>NO_DASHBOARDS_WARNING</span></p>\r\n<div class=\"widgets-grid-container\">\r\n    <gridster [options]=\"defaultGridsterConfig\" #gridster>\r\n        <gridster-item *ngFor=\"let item of layout; index as i\" [item]=\"item\">\r\n            <div class=\"widget-header aza-cmp-flex-row\">\r\n                <div *ngIf=\"getWidgetInfo(i).loading\" class=\"widget-loading\">\r\n                    <img [src]=\"widgetDataLoadingImageUrl\" class=\"widget-loading-img\"/>\r\n                </div>\r\n                <div class=\"widget-title-menu aza-cmp-flex-row aza-cmp-flex-item-grow aza-cmp-flex-item-content-end\">\r\n                    <span>{{widgets[i]?.title}}</span>\r\n                    <button\r\n                        mat-icon-button\r\n                        (click)=\"widgetMenuSelected(i)\"\r\n                        [matMenuTriggerFor]=\"widgetActionsMenu\"\r\n                    >\r\n                        <mat-icon>more_vert</mat-icon>\r\n                    </button>\r\n                </div>\r\n            </div>\r\n            <azavista-widget-placeholder\r\n                [definition]=\"widgets[i]\"\r\n                (loading)=\"onWidgetLoading(i, $event)\"\r\n                (settingsNeeded)=\"onSettingsNeeded(i)\"\r\n                (customEvent)=\"onWidgetCustomEvent(i, $event)\"\r\n            ></azavista-widget-placeholder>\r\n        </gridster-item>\r\n    </gridster>\r\n</div>\r\n\r\n<mat-menu #widgetActionsMenu=\"matMenu\">\r\n    <ng-template matMenuContent>\r\n        <button mat-menu-item (click)=\"viewDetails(selectedWidgetMenuIndex)\" [disabled]=\"disabledWidgetActions?.view\">\r\n            <mat-icon matPrefix [color]=\"disabledWidgetActions?.view ? '' : 'primary'\">visibility</mat-icon>\r\n            {{ getViewButtonText(selectedWidgetMenuIndex) }}\r\n        </button>\r\n        <button mat-menu-item (click)=\"showWidgetConfig(selectedWidgetMenuIndex)\" [disabled]=\"disabledWidgetActions?.edit\">\r\n            <mat-icon matPrefix [color]=\"disabledWidgetActions?.edit ? '' : 'primary'\">edit</mat-icon>\r\n            {{ 'EDIT' | translate }}\r\n        </button>\r\n        <button (click)=\"deleteSelectedWidget(selectedWidgetMenuIndex)\" mat-menu-item [disabled]=\"disabledWidgetActions?.delete\">\r\n            <mat-icon matPrefix [color]=\"disabledWidgetActions?.delete ? '' : 'warn'\">delete</mat-icon>\r\n            {{ 'DELETE' | translate }}\r\n        </button>\r\n    </ng-template>\r\n</mat-menu>\r\n\r\n<ng-template #addWidgetButtonTemplate>\r\n    <azavista-button\r\n        (click)=\"addWidget()\" color=\"primary\" [disabled]=\"!dashboards.length || disableAddWidget\">\r\n        <mat-icon>add</mat-icon><span translate>ADD_WIDGET</span>\r\n    </azavista-button>\r\n</ng-template>\r\n", dependencies: [{ kind: "directive", type: i4.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i4.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: i5.MatMenu, selector: "mat-menu", exportAs: ["matMenu"] }, { kind: "component", type: i5.MatMenuItem, selector: "[mat-menu-item]", inputs: ["disabled", "disableRipple", "role"], exportAs: ["matMenuItem"] }, { kind: "directive", type: i5.MatMenuContent, selector: "ng-template[matMenuContent]" }, { kind: "directive", type: i5.MatMenuTrigger, selector: "[mat-menu-trigger-for], [matMenuTriggerFor]", exportAs: ["matMenuTrigger"] }, { kind: "component", type: i6.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { kind: "component", type: i7.GridsterComponent, selector: "gridster", inputs: ["options"] }, { kind: "component", type: i7.GridsterItemComponent, selector: "gridster-item", inputs: ["item"], outputs: ["itemInit", "itemChange", "itemResize"] }, { kind: "directive", type: i8.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i8.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i9.TranslateDirective, selector: "[translate],[ngx-translate]", inputs: ["translate", "translateParams"] }, { kind: "component", type: i10.AzavistaButtonComponent, selector: "azavista-button", inputs: ["acl", "disabled", "cssClasses", "containerCssClasses", "buttonAttributes"] }, { kind: "component", type: i10.AzavistaFlatButtonComponent, selector: "azavista-flat-button" }, { kind: "component", type: i11.AzavistaWidgetPlaceholderComponent, selector: "azavista-widget-placeholder", inputs: ["definition", "configMode"], outputs: ["settingsChanged", "settingsValid", "loading", "settingsNeeded", "customEvent"] }, { kind: "component", type: i12.AzavistaExportedElementWrapperComponent, selector: "azavista-exported-element-wrapper", inputs: ["loadedElementRefType"] }, { kind: "component", type: i13.MatIconButton, selector: "button[mat-icon-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }, { kind: "component", type: i14.MatFormField, selector: "mat-form-field", inputs: ["hideRequiredMarker", "color", "floatLabel", "appearance", "subscriptSizing", "hintLabel"], exportAs: ["matFormField"] }, { kind: "directive", type: i14.MatLabel, selector: "mat-label" }, { kind: "directive", type: i14.MatPrefix, selector: "[matPrefix], [matIconPrefix], [matTextPrefix]", inputs: ["matTextPrefix"] }, { kind: "component", type: i15.MatSelect, selector: "mat-select", inputs: ["disabled", "disableRipple", "tabIndex", "panelWidth", "hideSingleSelectionIndicator"], exportAs: ["matSelect"] }, { kind: "component", type: i16.MatOption, selector: "mat-option", exportAs: ["matOption"] }, { kind: "pipe", type: i9.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: DashboardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'azavista-dashboard', template: "<div *ngIf=\"dashboardTitle\" class=\"title-container aza-cmp-flex-row aza-cmp-content-space-between\">\r\n    <h1 class=\"dashboard-title\">{{ dashboardTitle }}</h1>\r\n    <ng-container *ngTemplateOutlet=\"addWidgetButtonTemplate\"></ng-container>\r\n</div>\r\n\r\n<azavista-exported-element-wrapper>\r\n    <div class=\"actions-container dashboard-actions-container aza-cmp-flex-row aza-cmp-content-space-between aza-cmp-rounded-border\">\r\n        <div class=\"dashboard-selector aza-cmp-flex-row\">\r\n            <mat-form-field class=\"aza-cmp-medium-width\">\r\n                <mat-label><span translate>DASHBOARD</span></mat-label>\r\n                <mat-select [(ngModel)]=\"selectedDashboard\" (selectionChange)=\"onDashboardSelected($event.value)\">\r\n                    <mat-option *ngFor=\"let dashboard of dashboards\" [value]=\"dashboard\">{{ dashboard.name }}</mat-option>\r\n                </mat-select>\r\n            </mat-form-field>\r\n            <azavista-flat-button\r\n                [disabled]=\"disableEditDashboard || !selectedDashboard || !dashboards.length\" (click)=\"onEditDashboardClicked()\"\r\n                color=\"primary\">\r\n                <mat-icon class=\"material-icons-outlined\">edit</mat-icon><span>{{ 'EDIT_DASHBOARD' | translate }}</span>\r\n            </azavista-flat-button>\r\n            <azavista-flat-button (click)=\"onCreateDashboardClicked()\" color=\"primary\" [disabled]=\"disableCreateDashboard\">\r\n                <mat-icon class=\"material-icons-outlined\">add</mat-icon><span>{{ 'CREATE_DASHBOARD' | translate }}</span>\r\n            </azavista-flat-button>\r\n        </div>\r\n        <ng-container *ngIf=\"!dashboardTitle\">\r\n            <ng-container *ngTemplateOutlet=\"addWidgetButtonTemplate\"></ng-container>\r\n        </ng-container>\r\n        <div class=\"dashboard-config-options\">\r\n            <mat-form-field>\r\n                <mat-label><span translate>WIDGETS_ALIGNMENT</span></mat-label>\r\n                <mat-select [(ngModel)]=\"defaultGridsterConfig.compactType\" (ngModelChange)=\"onWidgetAlignmentChanged()\">\r\n                    <mat-option *ngFor=\"let compactOption of compactOptions\" [value]=\"compactOption.value\">\r\n                      {{ compactOption.label | translate }}\r\n                    </mat-option>\r\n                </mat-select>\r\n            </mat-form-field>\r\n        </div>\r\n    </div>\r\n</azavista-exported-element-wrapper>\r\n\r\n<p *ngIf=\"!dashboards.length\" class=\"no-dashboards-warning\"><span translate>NO_DASHBOARDS_WARNING</span></p>\r\n<div class=\"widgets-grid-container\">\r\n    <gridster [options]=\"defaultGridsterConfig\" #gridster>\r\n        <gridster-item *ngFor=\"let item of layout; index as i\" [item]=\"item\">\r\n            <div class=\"widget-header aza-cmp-flex-row\">\r\n                <div *ngIf=\"getWidgetInfo(i).loading\" class=\"widget-loading\">\r\n                    <img [src]=\"widgetDataLoadingImageUrl\" class=\"widget-loading-img\"/>\r\n                </div>\r\n                <div class=\"widget-title-menu aza-cmp-flex-row aza-cmp-flex-item-grow aza-cmp-flex-item-content-end\">\r\n                    <span>{{widgets[i]?.title}}</span>\r\n                    <button\r\n                        mat-icon-button\r\n                        (click)=\"widgetMenuSelected(i)\"\r\n                        [matMenuTriggerFor]=\"widgetActionsMenu\"\r\n                    >\r\n                        <mat-icon>more_vert</mat-icon>\r\n                    </button>\r\n                </div>\r\n            </div>\r\n            <azavista-widget-placeholder\r\n                [definition]=\"widgets[i]\"\r\n                (loading)=\"onWidgetLoading(i, $event)\"\r\n                (settingsNeeded)=\"onSettingsNeeded(i)\"\r\n                (customEvent)=\"onWidgetCustomEvent(i, $event)\"\r\n            ></azavista-widget-placeholder>\r\n        </gridster-item>\r\n    </gridster>\r\n</div>\r\n\r\n<mat-menu #widgetActionsMenu=\"matMenu\">\r\n    <ng-template matMenuContent>\r\n        <button mat-menu-item (click)=\"viewDetails(selectedWidgetMenuIndex)\" [disabled]=\"disabledWidgetActions?.view\">\r\n            <mat-icon matPrefix [color]=\"disabledWidgetActions?.view ? '' : 'primary'\">visibility</mat-icon>\r\n            {{ getViewButtonText(selectedWidgetMenuIndex) }}\r\n        </button>\r\n        <button mat-menu-item (click)=\"showWidgetConfig(selectedWidgetMenuIndex)\" [disabled]=\"disabledWidgetActions?.edit\">\r\n            <mat-icon matPrefix [color]=\"disabledWidgetActions?.edit ? '' : 'primary'\">edit</mat-icon>\r\n            {{ 'EDIT' | translate }}\r\n        </button>\r\n        <button (click)=\"deleteSelectedWidget(selectedWidgetMenuIndex)\" mat-menu-item [disabled]=\"disabledWidgetActions?.delete\">\r\n            <mat-icon matPrefix [color]=\"disabledWidgetActions?.delete ? '' : 'warn'\">delete</mat-icon>\r\n            {{ 'DELETE' | translate }}\r\n        </button>\r\n    </ng-template>\r\n</mat-menu>\r\n\r\n<ng-template #addWidgetButtonTemplate>\r\n    <azavista-button\r\n        (click)=\"addWidget()\" color=\"primary\" [disabled]=\"!dashboards.length || disableAddWidget\">\r\n        <mat-icon>add</mat-icon><span translate>ADD_WIDGET</span>\r\n    </azavista-button>\r\n</ng-template>\r\n" }]
        }], ctorParameters: function () { return [{ type: i1.MatDialog }, { type: i2.Router }, { type: i3.TranslationService }, { type: i0.NgZone }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [AZAVISTA_COMPONENTS_DASHBOARD_DEFAULT_OPTIONS]
                }] }]; }, propDecorators: { dashboardTitle: [{
                type: Input
            }], activeDashboardId: [{
                type: Input
            }], dashboards: [{
                type: Input
            }], gridsterConfig: [{
                type: Input
            }], availableWidgets: [{
                type: Input
            }], layout: [{
                type: Input
            }], widgets: [{
                type: Input
            }], widgetDataLoadingImageUrl: [{
                type: Input
            }], disableAddWidget: [{
                type: Input
            }], disableEditDashboard: [{
                type: Input
            }], disableCreateDashboard: [{
                type: Input
            }], disabledWidgetActions: [{
                type: Input
            }], dashboardChanged: [{
                type: Output
            }], dashboardSelected: [{
                type: Output
            }], editDashboard: [{
                type: Output
            }], createDashboard: [{
                type: Output
            }], widgetCustomEvent: [{
                type: Output
            }], gridster: [{
                type: ViewChild,
                args: ['gridster', { static: true }]
            }] } });

class AzavistaDashboardModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaDashboardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.10", ngImport: i0, type: AzavistaDashboardModule, declarations: [DashboardComponent], imports: [CommonModule,
            MatMenuModule,
            MatIconModule,
            GridsterModule,
            AzavistaSharedModule,
            AzavistaMultiSelectDialogModule,
            AzavistaButtonModule,
            AzavistaWidgetsModule,
            AzavistaExportedElementModule,
            MatButtonModule,
            MatFormFieldModule,
            MatSelectModule], exports: [DashboardComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaDashboardModule, imports: [CommonModule,
            MatMenuModule,
            MatIconModule,
            GridsterModule,
            AzavistaSharedModule,
            AzavistaMultiSelectDialogModule,
            AzavistaButtonModule,
            AzavistaWidgetsModule,
            AzavistaExportedElementModule,
            MatButtonModule,
            MatFormFieldModule,
            MatSelectModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaDashboardModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [DashboardComponent],
                    exports: [
                        DashboardComponent
                    ],
                    imports: [
                        CommonModule,
                        MatMenuModule,
                        MatIconModule,
                        GridsterModule,
                        AzavistaSharedModule,
                        AzavistaMultiSelectDialogModule,
                        AzavistaButtonModule,
                        AzavistaWidgetsModule,
                        AzavistaExportedElementModule,
                        MatButtonModule,
                        MatFormFieldModule,
                        MatSelectModule
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AZAVISTA_COMPONENTS_DASHBOARD_DEFAULT_OPTIONS, AzavistaDashboardModule, DashboardComponent, NG_GRIDSTER_COMPACT_OPTIONS };
